import React, { useEffect, useMemo } from 'react'
import { Link, useForm, usePage } from '@inertiajs/inertia-react'
import DatePicker, { registerLocale } from 'react-datepicker'
import es from 'date-fns/locale/es'
import 'react-datepicker/dist/react-datepicker.css'
import AlertDismissible from '../../../Shared/Components/AlertDismissible'
import BasicRichTextEditor from '../../../Shared/Components/BasicRichTextEditor'
import Breadcrumbs from '../../../Shared/Components/Breadcrumbs'
import FormValidationSummary, {
    mergeValidationErrors,
    validationMessage,
} from '../../../Shared/Components/FormValidationSummary'
import Layout from '../../../Shared/Layout/LayoutAdmin'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Row from 'react-bootstrap/Row'
import { contrastTextColor, fromYmd, toYmd } from '../../../utils/announcementsPermissions'
import '../announcements.css'

registerLocale('es', es)

const AnnouncementForm = ({ sections, today, authorName, maxImages }) => {
    const { flash, errors: pageErrors } = usePage().props

    const form = useForm({
        title: '',
        announcement_section_id: '',
        published_on: today,
        content: '',
        images: [],
    })

    const previews = useMemo(() => form.data.images.map((file) => URL.createObjectURL(file)), [form.data.images])
    useEffect(() => () => previews.forEach((url) => URL.revokeObjectURL(url)), [previews])

    const allErrors = mergeValidationErrors(form.errors, pageErrors)
    const fieldError = (key) => validationMessage(allErrors, key)
    const imageErrors = Object.entries(allErrors || {})
        .filter(([key]) => key === 'images' || key.startsWith('images.'))
        .map(([, message]) => (Array.isArray(message) ? message[0] : message))

    const selectedSection = sections.find((s) => String(s.id) === String(form.data.announcement_section_id))

    const addImages = (e) => {
        const files = Array.from(e.target.files || [])
        e.target.value = ''
        if (!files.length) return
        form.setData('images', [...form.data.images, ...files].slice(0, maxImages))
    }

    const removeImage = (index) => {
        form.setData(
            'images',
            form.data.images.filter((_, i) => i !== index),
        )
    }

    const submit = (e) => {
        e.preventDefault()
        form.post(route('announcements.manage.store'), { forceFormData: true, preserveScroll: true })
    }

    return (
        <Row>
            {flash?.error && <AlertDismissible message={flash.error} type="danger" />}
            <FormValidationSummary errors={allErrors} />
            <span className="pb-7 pt-7">
                <Breadcrumbs currentPath="admin/comunicados/administrar/nuevo" />
            </span>

            <Card className="card-flush shadow-sm mb-10">
                <Card.Header className="pt-7">
                    <Card.Title className="fw-bold text-gray-800">Nuevo comunicado</Card.Title>
                </Card.Header>
                <Card.Body className="py-8 px-6 px-lg-10">
                    <Form onSubmit={submit}>
                        <Row className="g-6 mb-6">
                            <Col lg={12}>
                                <Form.Label className="fw-bold required">Título</Form.Label>
                                <Form.Control
                                    value={form.data.title}
                                    onChange={(e) => form.setData('title', e.target.value)}
                                    maxLength={255}
                                    isInvalid={Boolean(fieldError('title'))}
                                    placeholder="Asunto del comunicado"
                                />
                                <Form.Control.Feedback type="invalid">{fieldError('title')}</Form.Control.Feedback>
                            </Col>
                            <Col md={4}>
                                <Form.Label className="fw-bold required">Sección</Form.Label>
                                <Form.Select
                                    value={form.data.announcement_section_id}
                                    onChange={(e) => form.setData('announcement_section_id', e.target.value)}
                                    isInvalid={Boolean(fieldError('announcement_section_id'))}
                                    style={
                                        selectedSection
                                            ? { borderLeft: `6px solid ${selectedSection.color}` }
                                            : undefined
                                    }
                                >
                                    <option value="">Selecciona…</option>
                                    {sections.map((s) => (
                                        <option key={s.id} value={s.id}>
                                            {s.name}
                                        </option>
                                    ))}
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">
                                    {fieldError('announcement_section_id')}
                                </Form.Control.Feedback>
                                {selectedSection && (
                                    <span
                                        className="badge mt-2"
                                        style={{
                                            backgroundColor: selectedSection.color,
                                            color: contrastTextColor(selectedSection.color),
                                        }}
                                    >
                                        {selectedSection.name}
                                    </span>
                                )}
                            </Col>
                            <Col md={4}>
                                <Form.Label className="fw-bold required">Fecha</Form.Label>
                                <DatePicker
                                    className={`form-control ${fieldError('published_on') ? 'is-invalid' : ''}`}
                                    locale="es"
                                    dateFormat="dd/MM/yyyy"
                                    selected={fromYmd(form.data.published_on)}
                                    maxDate={fromYmd(today)}
                                    onChange={(date) => form.setData('published_on', toYmd(date))}
                                />
                                {fieldError('published_on') && (
                                    <div className="invalid-feedback d-block">{fieldError('published_on')}</div>
                                )}
                                <div className="form-text">Solo hoy o fechas anteriores.</div>
                            </Col>
                            <Col md={4}>
                                <Form.Label className="fw-bold">Autor</Form.Label>
                                <Form.Control value={authorName} readOnly disabled />
                            </Col>
                        </Row>

                        <div className="mb-6">
                            <Form.Label className="fw-bold required">Texto</Form.Label>
                            <BasicRichTextEditor
                                value={form.data.content}
                                onChange={(html) => form.setData('content', html)}
                                placeholder="Escribe el comunicado…"
                                isInvalid={Boolean(fieldError('content'))}
                            />
                            {fieldError('content') && (
                                <div className="invalid-feedback d-block">{fieldError('content')}</div>
                            )}
                        </div>

                        <div className="mb-8">
                            <Form.Label className="fw-bold">
                                Imágenes <span className="text-muted fw-normal">(opcional, máximo {maxImages})</span>
                            </Form.Label>
                            <div className="d-flex flex-wrap gap-4 align-items-start">
                                {previews.map((url, index) => (
                                    <div key={url} className="position-relative">
                                        <img src={url} alt={`Imagen ${index + 1}`} className="announcements-thumb" />
                                        <Button
                                            size="sm"
                                            variant="danger"
                                            className="btn-icon position-absolute top-0 end-0 m-1 rounded-circle"
                                            style={{ width: 26, height: 26 }}
                                            title="Quitar"
                                            onClick={() => removeImage(index)}
                                        >
                                            <i className="bi bi-x-lg fs-8"></i>
                                        </Button>
                                    </div>
                                ))}
                                {form.data.images.length < maxImages && (
                                    <label
                                        className="announcements-thumb d-flex flex-column align-items-center justify-content-center text-gray-500 bg-light"
                                        style={{ cursor: 'pointer', borderStyle: 'dashed' }}
                                    >
                                        <i className="bi bi-image fs-2 mb-1"></i>
                                        <span className="fs-8">Agregar</span>
                                        <input
                                            type="file"
                                            accept="image/jpeg,image/png,image/webp"
                                            multiple
                                            className="d-none"
                                            onChange={addImages}
                                        />
                                    </label>
                                )}
                            </div>
                            {imageErrors.length > 0 && (
                                <div className="invalid-feedback d-block">{imageErrors.join(' ')}</div>
                            )}
                            <div className="form-text">JPG, PNG o WEBP, máximo 5 MB cada una. Se redimensionan a 1600 px de ancho.</div>
                        </div>

                        <div className="d-flex gap-3">
                            <Button type="submit" variant="primary" disabled={form.processing}>
                                {form.processing ? 'Publicando…' : 'Publicar comunicado'}
                            </Button>
                            <Link href={route('announcements.manage')} className="btn btn-light">
                                Cancelar
                            </Link>
                        </div>
                        <div className="form-text mt-3">
                            Los comunicados no se pueden editar después de publicarse; solo eliminar.
                        </div>
                    </Form>
                </Card.Body>
            </Card>
        </Row>
    )
}

AnnouncementForm.layout = (page) => <Layout title="Nuevo comunicado">{page}</Layout>

export default AnnouncementForm
